import { Box, Typography, Paper, Link } from '@mui/material';
import { CheckCircle, Clock, Inbox, AlertCircle, Loader } from 'lucide-react';
import type { FundSubmissionStatus } from '@lingaai/supabase-client';

interface CollectionNarrativeProps {
  submissions: FundSubmissionStatus[];
  deadline: Date;
  onFilterStatus: (status: string | null) => void;
}

export function CollectionNarrative({ submissions, deadline, onFilterStatus }: CollectionNarrativeProps) {
  // Calculate narrative data - only count actual submissions, not all portfolio companies
  const actualSubmissions = submissions.filter((s) => s.submission_id !== null);
  const totalCompanies = actualSubmissions.length;
  const responded = actualSubmissions.filter((s) => s.submitted_at !== null);
  const notResponded = actualSubmissions.filter((s) => s.submitted_at === null && s.status !== 'draft');

  const complete = actualSubmissions.filter((s) => s.status === 'completed');
  const pendingReview = actualSubmissions.filter((s) => s.status === 'needs_review');
  const processing = actualSubmissions.filter((s) => s.status === 'processing');
  const overdue = actualSubmissions.filter((s) => s.email_status === 'overdue');

  // Find earliest request date (only from actual submissions)
  const earliestRequest = actualSubmissions.reduce(
    (earliest, s) => {
      if (!s.created_at) return earliest;
      const date = new Date(s.created_at);
      return !earliest || date < earliest ? date : earliest;
    },
    null as Date | null
  );

  // Find latest reminder date (only from actual submissions)
  const latestReminder = actualSubmissions.reduce(
    (latest, s) => {
      if (!s.last_email_sent_at) return latest;
      const date = new Date(s.last_email_sent_at);
      return !latest || date > latest ? date : latest;
    },
    null as Date | null
  );

  // Calculate deadline status
  const now = new Date();
  const diffTime = deadline.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const isOverdue = diffDays < 0;

  const formatDate = (date: Date | null) => {
    if (!date) return 'Unknown';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const ClickableText = ({ children, onClick, color }: { children: React.ReactNode; onClick: () => void; color?: string }) => (
    <Link
      component="button"
      onClick={onClick}
      sx={{
        color: color || 'inherit',
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
        fontWeight: 'inherit',
        fontSize: 'inherit',
      }}
    >
      {children}
    </Link>
  );

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* Timeline container */}
        <Box sx={{ position: 'relative', pl: 3 }}>
          {/* Vertical timeline line */}
          <Box
            sx={{
              position: 'absolute',
              left: '9px',
              top: '9px',
              bottom: '9px',
              width: '2px',
              bgcolor: 'divider',
            }}
          />

          {/* Initial requests sent */}
          <Box sx={{ position: 'relative', mb: 2, display: 'flex', alignItems: 'flex-start' }}>
            <Box
              sx={{
                position: 'absolute',
                left: '-24px',
                top: '2px',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Inbox size={18} strokeWidth={1.5} />
            </Box>
            <Typography variant="body1" fontWeight="medium">
              Initial requests sent to {totalCompanies} {totalCompanies === 1 ? 'company' : 'companies'}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                on {formatDate(earliestRequest)}
              </Typography>
            </Typography>
          </Box>

          {/* Responded section */}
          {responded.length > 0 && (
            <Box sx={{ position: 'relative', mb: 2, display: 'flex', alignItems: 'flex-start' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: '-24px',
                  top: '2px',
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Inbox size={18} strokeWidth={1.5} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <ClickableText onClick={() => onFilterStatus('responded')}>
                  <Typography variant="body1" fontWeight="medium">
                    {responded.length} responded
                  </Typography>
                </ClickableText>

                {/* Nested items */}
                {(complete.length > 0 || processing.length > 0 || pendingReview.length > 0) && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 2, mt: 0.5 }}>
                    {complete.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle size={16} strokeWidth={1.5} color="#2e7d32" />
                        <ClickableText onClick={() => onFilterStatus('completed')}>
                          <Typography variant="body2">{complete.length} complete</Typography>
                        </ClickableText>
                      </Box>
                    )}

                    {processing.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Loader size={16} strokeWidth={1.5} color="#1976d2" />
                        <ClickableText onClick={() => onFilterStatus('processing')}>
                          <Typography variant="body2">{processing.length} processing</Typography>
                        </ClickableText>
                      </Box>
                    )}

                    {pendingReview.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Clock size={16} strokeWidth={1.5} color="#ed6c02" />
                        <ClickableText onClick={() => onFilterStatus('needs_review')}>
                          <Typography variant="body2">{pendingReview.length} pending review</Typography>
                        </ClickableText>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Reminder sent (if applicable) */}
          {latestReminder && notResponded.length > 0 && (
            <Box sx={{ position: 'relative', mb: 2, display: 'flex', alignItems: 'flex-start' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: '-24px',
                  top: '2px',
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AlertCircle size={18} strokeWidth={1.5} color="#ed6c02" />
              </Box>
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Reminder sent to {notResponded.length} {notResponded.length === 1 ? 'company' : 'companies'}{' '}
                  <Typography component="span" variant="body2" color="text.secondary">
                    on {formatDate(latestReminder)}
                  </Typography>
                </Typography>
              </Box>
            </Box>
          )}

          {/* Not responded / Overdue section */}
          {notResponded.length > 0 && (
            <Box sx={{ position: 'relative', mb: 2, display: 'flex', alignItems: 'flex-start' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: '-24px',
                  top: '2px',
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isOverdue || overdue.length > 0 ? <AlertCircle size={18} strokeWidth={1.5} color="#d32f2f" /> : <Clock size={18} strokeWidth={1.5} />}
              </Box>
              <Box>
                <ClickableText onClick={() => onFilterStatus('not_responded')}>
                  <Typography variant="body1" fontWeight="medium" color={isOverdue || overdue.length > 0 ? 'error' : 'text.primary'}>
                    {overdue.length > 0
                      ? `${overdue.length} ${overdue.length === 1 ? 'company' : 'companies'} overdue`
                      : `${notResponded.length} ${notResponded.length === 1 ? 'company' : 'companies'} not responded`}
                  </Typography>
                </ClickableText>
                <Typography variant="body2" color="text.secondary">
                  {diffDays > 0 ? `Deadline: ${formatDate(deadline)} (${diffDays} days remaining)` : `${Math.abs(diffDays)} days overdue`}
                </Typography>
              </Box>
            </Box>
          )}

          {/* All complete state */}
          {responded.length === totalCompanies && complete.length === totalCompanies && (
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: '-24px',
                  top: '2px',
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircle size={18} strokeWidth={1.5} color="#2e7d32" />
              </Box>
              <ClickableText onClick={() => onFilterStatus(null)}>
                <Typography variant="body1" fontWeight="medium" color="success.main">
                  All {totalCompanies} companies completed
                </Typography>
              </ClickableText>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
